import React, { forwardRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import useRootSelector from 'common/hooks/useRootSelector';
import { alpha, Paper, StyledComponentProps } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { DateInput, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/common';
import FullCalendar from '@fullcalendar/react';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Loader } from 'common/components';
import { EventBlock } from 'modules/booking/components';
import { IBookingEvent } from 'modules/booking/interfaces';
import { selectLocale, selectTimezone } from 'common/state/settings/selectors';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import messages from 'common/messages/messages';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import usePermissionContext from 'common/hooks/context/usePermissionContext';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      height: 600,
      paddingBottom: theme.spacing(13),
    },
    '& .fc-scrollgrid-section-header': {
      '& .fc-scroller': {
        overflow: 'hidden',
        'scrollbar-width': 'none',
        '-ms-overflow-style': 'none',

        '&::-webkit-scrollbar': {
          width: 0,
          height: 0,
        },
      },
    },
    '& .fc, .fc-view-harness': {
      overflow: 'auto',
      borderRadius: '3px',
      height: '100%',
    },
    '& .fc-view': {
      backgroundColor: 'white',
    },
    '& .fc .fc-scrollgrid': {
      border: 'none',
    },
    '& .fc .fc-button-primary, .fc .fc-button-primary:not(:disabled).fc-button-active': {
      width: '65px',
      border: 'none',
      backgroundColor: 'white',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.1)',
      fontSize: '14px',
      fontWeight: '500',
      color: theme.palette.text.primary,
    },
    '& .fc .fc-button-primary:not(:disabled).fc-button-active, .fc .fc-button-primary:hover': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
    '& .fc .fc-button-primary:not(:disabled):active:focus, .fc .fc-button-primary:not(:disabled).fc-button-active:focus': {
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.1)',
    },
    '& .fc .fc-button-primary:not(:disabled):active, .fc .fc-button-primary:not(:disabled).fc-button-active': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
    '& .fc .fc-button-primary:disabled': {
      backgroundColor: 'white',
      color: theme.palette.text.disabled,
    },
    '& .fc .fc-button-primary:focus, .fc-button:focus': {
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.1)',
    },
    '& .fc-timegrid-axis': {
      border: 'none',
    },
    '& .fc-col-header-cell': {
      padding: '12px',
      borderLeft: 'none',
      borderRight: 'none',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 2,
        paddingRight: 2,
      },

      '& .fc-col-header-cell-cushion': {
        fontWeight: 'normal',
        color: theme.palette.text.primary,
        [theme.breakpoints.down('xs')]: {
          maxWidth: 28,
          padding: 0,
        },
      },
    },
    '& .fc-col-header-cell.fc-day-today': {
      '& .fc-col-header-cell-cushion': {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
      },
    },
    '& .fc-col-header-cell.fc-day-past': {
      '& .fc-col-header-cell-cushion': {
        opacity: '0.5',
      },
    },
    '& .fc .fc-timegrid-col.fc-day-today, .fc .fc-daygrid-day.fc-day-today': {
      background: 'none',
    },
    '& .fc-timegrid-slot': {
      '& .fc-timegrid-slot-label-cushion': {
        fontSize: '0.6rem',
        color: theme.palette.text.disabled,
      },
    },
    '& .fc-timegrid-cols': {
      zIndex: 2,

      '& .fc-timegrid-col.fc-day-today, .fc-timegrid-col.fc-day-future': {
        cursor: 'pointer',
      },
    },
    '& .fc-daygrid-day.fc-day-today, .fc-daygrid-day.fc-day-future': {
      cursor: 'pointer',
    },
    '& .fc .fc-timegrid-slot-label': {
      borderTop: 'none',
    },
    '& .fc-day-today .fc-daygrid-day-number': {
      color: theme.palette.primary.main,
    },
    '& .fc .fc-timegrid-slot-minor': {
      borderTop: 'none',
    },

    '& .fc-event': {
      minHeight: '23px',
      overflow: 'hidden',

      '&.fc-event-canceled': {
        opacity: '0.4',
      },

      '&:hover': {
        cursor: 'pointer',
      },
      [theme.breakpoints.down('xs')]: {
        '& .fcAvatarGroup, & .fcParticipants, & .fcTitle': {
          display: 'none',
        },
      },
    },

    '& .fc-timegrid-now-indicator-container': {
      overflow: 'visible',
    },

    '& .fc-timegrid-col.fc-day-past': {
      opacity: 0.65,
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },

    '& .fc-daygrid-day.fc-day-past': {
      opacity: 0.65,
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },

    '& .canceled': {
      '& .fc-event-main': {
        opacity: 0.5,
      },
    },

    '--fc-now-indicator-color': theme.palette.primary.main,
    '& .fc-timegrid-now-indicator-line': {
      marginTop: '-2px',
      borderWidth: '1px 0 1px 0',
      borderColor: 'white',
      position: 'relative',
      padding: '1px 0',
      backgroundColor: theme.palette.primary.main,
      '&:before': {
        content: '""',
        width: theme.spacing(1.875),
        height: theme.spacing(1.875),
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        position: 'absolute',
        top: theme.spacing(-0.8125),
        left: theme.spacing(-1),
        border: '1px solid white',
      },
      '&:after': {
        content: '""',
        padding: '1px 0',
        position: 'absolute',
        left: '5px',
        top: '0',
        borderWidth: '0 2px 0 2px',
        borderStyle: 'solid',
        borderColor: theme.palette.primary.main,
      },
    },
    '& .fc-event-main > div > div p': {
      color: 'white',
    },
    '& .fcParticipants p': {
      color: 'white',
    },
  },
}));

// TODO CALENDAR in fullcalendar v5 viewsOptions was changed, need to change options config
/* const viewsOptions = {
  timeGridDay: {
    dayHeaderFormat: { day: 'numeric', weekday: 'short', omitCommas: true },
  },
  timeGridWeek: {
    dayHeaderFormat: { day: 'numeric', weekday: 'short', omitCommas: true },
  },
  dayGridMonth: {
    dayHeaderFormat: { weekday: 'short' },
  },
}; */

const viewsOptions = {
  timeGridDay: {
    day: 'numeric',
    weekday: 'short',
    omitCommas: true,
  },
  timeGridWeek: {
    day: 'numeric',
    weekday: 'short',
    omitCommas: true,
  },
  dayGridMonth: {
    weekday: 'short',
  },
};

export type CalendarView = 'timeGridWeek' | 'timeGridDay' | 'dayGridMonth';

interface IBookingCalendarProps extends StyledComponentProps {
  bookingEvents: IBookingEvent[];
  initialView: CalendarView;
  initialDate?: DateInput;
  validRange?: {
    start?: DateInput;
    end?: DateInput;
  };
  isLoading?: boolean;
  onEventClick?: (e) => void;
  onDateClick?: (e) => void;
  onDatesRangeChange?: (datesSet: DatesSetArg) => void;
  timezone?: string;
  className?: string;
}

const BookingCalendar = forwardRef<any, IBookingCalendarProps>(
  (
    {
      bookingEvents,
      initialView,
      initialDate,
      validRange,
      isLoading,
      onEventClick,
      onDateClick,
      onDatesRangeChange,
      timezone,
      className = '',
    }: IBookingCalendarProps,
    ref,
  ) => {
    const classes = useStyles();
    const { BOOKING_EVENTS_CREATE } = usePermissionContext();
    const currentTimezoneView: string = useRootSelector(selectTimezone);
    const locale = useRootSelector(selectLocale);
    const renderIntlMessage = useRenderIntlMessage();
    const unlimitedLabel = renderIntlMessage(messages.unlimitedLabel);
    const [timezoneMoment] = useTimezoneMoment();
    let onCalendarEventClick: ((args: EventClickArg) => void) | undefined;

    if (onEventClick) {
      onCalendarEventClick = ({ event }) => {
        onEventClick({
          startDate: event.extendedProps.utcDate,
          eventId: event.extendedProps.eventId,
        });
      };
    }

    const hasEventHandler = !!onCalendarEventClick;

    const getEventClassNames = useCallback(({ event }) => {
      const cancelClassName = event.extendedProps?.status === 'CANCELED' ? 'fc-event-canceled' : '';

      return [cancelClassName];
    }, []);

    const events: EventInput[] = useMemo(() => {
      return bookingEvents.map(
        (event): EventInput => ({
          ...event,
          id: `${event.id}_${String(event.date)}`, // should be unique
          eventId: event.id,
          start: timezoneMoment(event.date).format(),
          end: timezoneMoment(event.date)
            .add(event.durationInMinutes, 'minutes')
            .format(),
          className: classNames({
            canceled: event.canceled,
            active: !event.canceled,
            'cursor-default': !hasEventHandler,
          }),
          // needed to fetch event
          utcDate: event.date,
          bookedSpotsCounter: `${event.bookedSpots}/${
            event.spotLimited ? event.spotAmount : unlimitedLabel
          }`,
        }),
      );
    }, [bookingEvents, timezoneMoment, unlimitedLabel, hasEventHandler]);

    if (!onCalendarEventClick) {
      onCalendarEventClick = () => {};
    }

    return (
      <Paper className={classNames(classes.root, { [className]: !!className })} elevation={2}>
        {!!isLoading && <Loader />}

        <FullCalendar
          ref={ref}
          // needed for reinitialize component after changing timezone view
          key={currentTimezoneView}
          plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, momentTimezonePlugin]}
          initialView={initialView}
          initialDate={initialDate}
          validRange={validRange}
          events={events}
          slotMinTime="00:00:00"
          allDaySlot={false}
          eventDisplay="block"
          eventBorderColor="white"
          headerToolbar={false}
          eventContent={EventBlock}
          eventClassNames={getEventClassNames}
          views={viewsOptions}
          eventClick={onCalendarEventClick}
          dateClick={BOOKING_EVENTS_CREATE ? onDateClick : undefined}
          datesSet={onDatesRangeChange}
          nowIndicator
          timeZone={timezone || currentTimezoneView}
          timeZoneParam={timezone || currentTimezoneView}
          locale={locale}
        />
      </Paper>
    );
  },
);

export default React.memo(BookingCalendar);
